import "./Tables.css"
import "./DataTable.css"
import React, {useEffect, useState} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import { Popover, Tooltip } from "@mui/material";
import { IndicatorColours } from "../Indicators/IndicatorColours";
import { flagSpa } from "../../../redux/actions/spa.actions";
import { AppDispatch } from "../../../redux/store";
import { relativeTimestamp } from "./RelativeTimestamp";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import useRoleCheck from "../../../hooks/useRoleCheck";
import { MQTT_PREFIX } from "../../../services/mqtt/mqttconfig";
import { localizeSpa, shortPalName } from "../../../utils/brand";
import { DataTableFoot, SortButton } from "./DataTableParts";
import RegisterUserModal from "../../../pages/SpaDetails/components/RegisterUserModal";
import {
  AlertIcon,
  ColumnsIcon,
  EthernetIcon,
  FlagLineIcon,
  LeafIcon,
  MessageIcon,
  PencilIcon,
  SlidersIcon,
  UserPlusIcon,
  WifiIcon,
  WifiOffIcon,
} from "../Icons/Icons";

type Order = 'asc' | 'desc';
const headers = [
  {id: 'checked', label: '', align: 'center', sortable: false},
  {id: 'status', label: 'Status', align: 'center', sortable: false},
  {id: 'has_internet', label: 'Internet', align: 'left', sortable: false},
  {id: 'id', label: 'GUID', align: 'left', sortable: true},
  {id: 'dealership.name', label: 'Dealership', align: 'left', sortable: true},
  {id: 'user.name', label: 'User', align: 'left', sortable: true},
  {id: 'arctic_serial_number', label: 'Serial', align: 'left', sortable: true},
  {id: 'ip_address', label: 'IP', align: 'left', sortable: true},
  {id: 'nick_name', label: 'Nickname', align: 'left', sortable: true},
  {id: 'errors', label: 'Errors', align: 'center', sortable: false},
  {id: 'yocto_version', label: 'Yocto', align: 'left', sortable: true},
  {id: 'lpc_version', label: 'LPC', align: 'left', sortable: true},
  {id: 'spaboy_firmware_version', label: shortPalName, align: 'left', sortable: true},
  {id: 'econ', label: 'EcoMode', align: 'center', sortable: false},
  {id: 'sbHr', label: 'Manual Mode', align: 'center', sortable: false},
  {id: 'log_level', label: 'Log Level', align: 'left', sortable: false},
  {id: 'current', label: 'Amps', align: 'center', sortable: false},
  {id: 'sbpH', label: 'pH', align: 'center', sortable: false},
  {id: 'sbORP', label: 'ORP', align: 'center', sortable: false},
  {id: 'user_registered_at', label: 'Registered', align: 'left', sortable: false},
  {id: 'created_at', label: 'Added', align: 'left', sortable: true},
  {id: 'updated_at', label: 'Updated', align: 'left', sortable: false},
  {id: 'last_online', label: 'Last Online', align: 'left', sortable: false},
  {id: 'control', label: 'Control', align: 'center', sortable: false},
  {id: 'comments', label: 'Comments', align: 'center', sortable: false},
  {id: 'is_flagged', label: 'Flag', align: 'center', sortable: false},
  {id: 'edit', label: 'Edit', align: 'center', sortable: false},
];

const defaultHeaders = [
  'checked',
  'status',
  'user.name',
  'arctic_serial_number',
  'ip_address',
  'nick_name',
  'errors',
  'econ',
  'current',
  'sbpH',
  'sbORP',
  'created_at',
  'updated_at',
  'control',
  'comments',
  'is_flagged',
];

// Row actions stay pinned to the right edge while the table scrolls sideways (px, fits each heading)
const actionWidths: Record<string, number> = {
  control: 76,
  comments: 96,
  is_flagged: 60,
  edit: 60,
};

interface SpasTableProps {
  pageName?:string,
  setChecked?: any,
  checked?: any[],
  setDeleteOpen: any;
  setEditOpen: any;
  setSpaToModify: any;
  // Page-specific controls (filters toggle, bulk actions) shown left of the Columns button
  toolbar?: React.ReactNode;
}

const signalMapper = (signal: any) => {
  if (signal<=-80){
      return 'Very Poor'
  } else if (signal<=-70 && signal>-80) {
      return 'Weak'
  } else if (signal<=-60 && signal>-70) {
      return 'Fair'
  } else if (signal<=-50 && signal>-60) {
      return 'Good'
  } else if (signal>-50){
      return 'Excellent'
  }
  return null
}

const formatDate = (value: any) => value ? <span className="dt-date">{new Date(value).toLocaleString()}</span> : null;

const SpasTable: React.FC<SpasTableProps> = (
  {
    pageName='spas',
    setChecked=null,
    checked=null,
    setDeleteOpen,
    setEditOpen,
    setSpaToModify,
    toolbar,
  }
) => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const {data:spas,loading:isLoading, meta} = useSelector((state: RootState) => state.spas);
  const [searchParams, setSearchParams] = useSearchParams();

  const [headersToShow, setHeadersToShow] = useState<any[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  // Handle Spa Delete
  const {canAccess:canEditSpa} = usePermissionCheck(['update_any_spa']);
  const {canAccess:canSelectAnySpa} = useRoleCheck(['developer']);

  // Below admin, a spa with no user is registered from the table and edited on its details page
  const {canAccess:canEditUnownedSpa} = useRoleCheck(['admin','developer']);
  const {canAccess:canRegisterSpa} = usePermissionCheck(['register_to_user']);
  const [spaToRegister, setSpaToRegister] = useState<any>(null);
  const isLockedSpa = (spa:any) => !spa?.user_id && !canEditUnownedSpa;

  // Table Sort (read from the URL so the arrow survives a reload)
  const [order, setOrder] = useState<Order>(searchParams.get('sort_direction') === 'desc' ? 'desc' : 'asc');
  const [orderBy, setOrderBy] = useState(searchParams.get('sort_by') ?? '');
  const [allChecked, setAllChecked] = useState(false);

  useEffect(() => {
    const movedSpas = canSelectAnySpa ? [...spas] : [...spas].filter((spa:any)=>spa?.is_moved);
    const isAllChecked = Boolean(
      checked &&
      checked.length > 0 &&
      movedSpas.length > 0 &&
      checked.length === movedSpas.length
    );
    setAllChecked(isAllChecked);
  }, [checked, spas]);


  useEffect(() => {
    let local = localStorage.getItem('spa_headers');
    let newArray=['checked','status']; //permanent headers
    if (local){
      newArray = local.split(','); //split local string of headers
    } else {
      newArray = [...defaultHeaders]; //use default header settings
    }

    if (canEditSpa && !local){
      newArray.push('edit');
    }

    setHeadersToShow(newArray);
  }, [pageName,canEditSpa,setHeadersToShow]);

  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const totalPages = meta?.total_pages ?? 1;
  const total = meta?.total ?? 0;

  const goToPage = (value: number) => {
    searchParams.set("page", String(value));
    setSearchParams(searchParams);
    setChecked?.([]);
    setAllChecked(false);
  }

  const handleRequestSort = (header_id: string) => {
    const isAsc = orderBy === header_id && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(header_id);
    searchParams.set("sort_direction", newOrder);
    searchParams.set("sort_by", header_id);
    setSearchParams(searchParams);
  };

  const handleSetting = (e:any, val:any) => {
    let isToggledOn = e.target.checked;
    let newArray = [...headersToShow]
    if (isToggledOn) {
      newArray.push(val);
    } else {
      newArray = newArray.filter((header:string)=>header!==val);
    }
    localStorage.setItem('spa_headers',newArray.toString());
    setHeadersToShow(newArray);
  }

  const handleCheck = (spa:any) => {
    setAllChecked(false);
    let newArr = [...checked??[]];
    let found = checked?.find((checked_spa:any)=>spa?.id===checked_spa?.id)!==undefined;
    if(found){
      newArr = newArr.filter((checked_spa:any)=>checked_spa?.id!==spa?.id);
    } else {
      newArr.push(spa);
    }
    setChecked(newArr);
  }

  // Handle Selecting Users
  const checkAll = (e:any) => {
    setAllChecked(e.target.checked);
    setChecked(e.target.checked ? canSelectAnySpa ? [...spas] : [...spas].filter((spa:any)=>spa?.is_moved):[])
  }

  const isChecked=(spa:any)=>{
    return checked?.find((checked_spa:any)=>spa?.id===checked_spa?.id)!==undefined
  }

  const handleEditDelete = (spa:any, action: 'delete'|'edit') => {
    setSpaToModify(spa);
    action==="delete"? setDeleteOpen(true) : setEditOpen(true);
  }

  const showSetting = (header:any) => {
    if(header.id!=='checked'){
      if (header.id==='edit'){
        return canEditSpa;
      }
      return true;
    }
    return false;
  };

  const handleFlag = async (guid:string) => {
    await dispatch(flagSpa(guid));
  }

  const isBeta = MQTT_PREFIX.includes('beta');

  const isColdTub = (() => {
    const brand = localStorage.getItem('data-brand');
    if (brand) return brand === 'coldtub';
    return window.location.hostname.includes('mycoldtub');
  })();

  const redirectDomain = isColdTub ? 'mycoldtub.com' : 'myarcticspa.com';

  const redirectSiteUrl = isBeta
  ? `beta.${redirectDomain}/dealers`
  : `${redirectDomain}/dealers`;

  // ---- Columns ----
  const visibleHeaders = headers.filter((header)=>
    header.id === 'checked' ? Boolean(checked) : headersToShow.includes(header.id) && (header.id !== 'edit' || canEditSpa)
  );

  const pinned = visibleHeaders.map((header)=>header.id).filter((id)=>id in actionWidths);

  const pinStyle = (id: string) => {
    const index = pinned.indexOf(id);
    if (index < 0) return undefined;
    const right = pinned.slice(index + 1).reduce((sum, next) => sum + actionWidths[next], 0);
    return {'--dt-right': `${right}px`, '--dt-w': `${actionWidths[id]}px`} as React.CSSProperties;
  };

  const cellClass = (header: typeof headers[number]) => [
    header.align === 'center' ? 'dt-center' : '',
    header.id === 'checked' ? 'dt-check-cell' : '',
    pinned.includes(header.id) ? 'dt-pin' : '',
    pinned[0] === header.id ? 'dt-pin-start' : '',
  ].filter(Boolean).join(' ') || undefined;

  // In-app link: a plain click stays in this tab, modifier-clicks open a new one
  const appLink = (path: string, className: string, children: React.ReactNode, label?: string) => (
    <a
      href={path}
      className={className}
      aria-label={label}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey) return;
        e.preventDefault();
        navigate(path);
      }}
    >
      {children}
    </a>
  );

  const renderCell = (id: string, spa: any) => {
    const isV1 = !spa?.is_moved;

    // The errors page is still classic-only for v1 spas
    const spaLink = (path: string, className: string, children: React.ReactNode, label?: string) => isV1 ? (
      <a
        href={`https://${redirectSiteUrl}/spadetails.aspx?SpaGUID=${spa?.id}&ignore_redirect=true`}
        className={className}
        aria-label={label}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ) : appLink(path, className, children, label);

    // Live readings: say how fresh they are
    const fetched = (node: React.ReactElement) => isV1 ? node : (
      <Tooltip title={`Last fetched ${relativeTimestamp(spa?.last_fetched)}`} followCursor={true}>{node}</Tooltip>
    );

    const na = <span className="dt-na">N/A</span>;

    switch (id) {
      case 'checked':
        return (
          <input
            type="checkbox"
            className="dt-check"
            name="spa-check"
            checked={isChecked(spa)}
            onChange={()=>handleCheck(spa)}
            disabled={isV1 && !canSelectAnySpa}
            aria-label={`Select ${spa?.nick_name ?? spa?.id}`}
          />
        );

      case 'status': {
        const lastSeen = isV1 ? spa.last_online : spa.last_connected;
        const tip = isV1 ?
          (spa.last_online ? <div>Last online {relativeTimestamp(undefined, spa.last_online)}</div> : '')
          :
          <>
            <div>Last connected {spa.last_connected ? relativeTimestamp(undefined, spa.last_connected) : 'Unknown'}</div>
            <div>Last fetched {relativeTimestamp(spa?.last_fetched)}</div>
          </>;
        return (
          <Tooltip title={tip}>
            <span className={`dt-status ${spa.is_connected ? 'online' : ''}`}>
              <span className="dt-dot" aria-hidden="true"/>
              <span className="visually-hidden">{spa.is_connected ? 'Online' : 'Offline'}</span>
              {!spa.is_connected && lastSeen && relativeTimestamp(undefined, lastSeen)}
              {isV1 && <span className="dt-v1">V1</span>}
            </span>
          </Tooltip>
        );
      }

      case 'has_internet': {
        if (isV1) {
          const label = spa.is_connected ? 'Connected' : 'No internet';
          return (
            <span className={`dt-net ${spa.is_connected ? 'good' : 'bad'}`} title={label}>
              {spa.is_connected ? <WifiIcon/> : <WifiOffIcon/>}
              <span className="visually-hidden">{label}</span>
            </span>
          );
        }
        const quality = spa.signal_strength != null ? signalMapper(spa.signal_strength) : null;
        const label = spa.has_internet ? (spa.signal_strength != null ? 'Wi-Fi' : 'Wired') : 'No internet';
        return (
          <span className={`dt-net ${spa.has_internet ? 'good' : 'bad'}`} title={label}>
            {spa.has_internet ? (spa.signal_strength != null ? <WifiIcon/> : <EthernetIcon/>) : <WifiOffIcon/>}
            <span className="visually-hidden">{label}</span>
            {quality && <span className="dt-sub">{quality} ({spa.signal_strength})</span>}
            {!spa.has_internet && spa.last_online && <span className="dt-sub">{relativeTimestamp(undefined, spa.last_online)}</span>}
          </span>
        );
      }

      case 'id':
        return appLink(`/spa/${spa?.id}`, 'dt-guid', spa?.id);

      case 'dealership.name':
        return spa.dealership ? appLink(`/dealership/${spa.dealership.id}`, 'dt-link', spa.dealership?.name??spa.dealership?.id??'N/A') : na;

      case 'user.name':
        return spa.user ? appLink(`/user/${spa.user_id}`, 'dt-link', spa.user?.name ?? spa.user?.username ?? spa.user?.email ?? 'N/A') : na;

      case 'arctic_serial_number':
        return appLink(`/spa/${spa?.id}`, 'dt-serial', spa.blue_falls_serial_number??spa.arctic_serial_number??"N/A");

      case 'ip_address':
        return <span className="dt-mono">{spa.ip_address}</span>;

      case 'nick_name':
        return appLink(`/spa/${spa?.id}`, 'dt-name', spa?.nick_name);

      case 'errors':
        return fetched(spaLink(
          `/spa/${spa?.id}/errors`,
          `dt-errors ${spa.hasErrors ? 'bad' : ''}`,
          <AlertIcon/>,
          spa.hasErrors ? 'Has errors, view errors' : 'No errors, view errors',
        ));

      case 'yocto_version':
        return <span className="dt-mono">{spa.yocto_version}</span>;

      case 'lpc_version':
        return <span className="dt-mono">{spa.lpc_version}</span>;

      case 'spaboy_firmware_version':
        return <span className="dt-mono">{spa.spaboy_firmware_version}</span>;

      case 'econ': {
        if (isV1) return null;
        const isOn = Boolean(Number(spa.econ??0));
        return fetched(appLink(
          `/spa-control/${spa?.id}/peak-management`,
          `dt-eco ${isOn ? 'on' : ''}`,
          <><LeafIcon/><span className="visually-hidden">{isOn ? 'Eco mode on' : 'Eco mode off'}</span></>,
        ));
      }

      case 'sbHr':
        if (isV1) return null;
        return fetched(spa.sbHr>0 ? <span className="dt-pill caution">On</span> : <span className="dt-off">Off</span>);

      case 'log_level':
        if (isV1) return null;
        return fetched(<span className="dt-tag">{spa?.log_level??'INFO'}</span>);

      case 'current':
        if (isV1) return null;
        return fetched(<span className="dt-num">{spa.current ? Number(spa.current) / 100 : 0}</span>);

      case 'sbpH':
        if (isV1) return null;
        return fetched(
          <span className="dt-reading">
            {spa.sbpH ? <span className="dt-dot" style={{background: Object.values(IndicatorColours)[spa.sbpHind??0]}} aria-hidden="true"/> : null}
            {spa.sbpH ? Number(spa.sbpH) / 100 : na}
          </span>
        );

      case 'sbORP':
        if (isV1) return null;
        return fetched(
          <span className="dt-reading">
            {spa.sbORP ? <span className="dt-dot" style={{background: Object.values(IndicatorColours)[spa.sbORPind??0]}} aria-hidden="true"/> : null}
            {spa.sbORP ?? na}
          </span>
        );

      case 'user_registered_at':
        return formatDate(spa.user_registered_at);

      case 'created_at':
        return formatDate(spa.created_at);

      case 'updated_at':
        return formatDate(spa.updated_at);

      case 'last_online':
        return formatDate(spa.last_online);

      case 'control':
        // V1 spas are controlled in the portal too (over the legacy broker), so always the in-app page
        return (
          <Tooltip title="Spa control" arrow>
            {appLink(`/spa-control/${spa?.id}`, 'dt-act', <SlidersIcon/>, 'View control')}
          </Tooltip>
        );

      case 'comments':
        return (
          <Tooltip title={`${spa?.comments_count ?? 0} comments · ${spa?.unread_count ?? 0} unread · ${spa?.unresolved_count ?? 0} unresolved`} arrow>
            {appLink(
              `/spa/${spa?.id}/comments`,
              'dt-act',
              <><MessageIcon/>{(spa?.unread_count ?? 0) > 0 && <span className="dt-badge">{spa.unread_count}</span>}</>,
              'View comments',
            )}
          </Tooltip>
        );

      case 'is_flagged':
        return (
          <Tooltip title={spa?.is_flagged ? 'Unflag spa' : 'Flag spa'} arrow>
            <button
              type="button"
              className={`dt-act ${spa?.is_flagged ? 'flagged' : ''}`}
              onClick={()=>handleFlag(spa?.id)}
              aria-label={spa?.is_flagged ? 'Unflag spa' : 'Flag spa'}
              aria-pressed={Boolean(spa?.is_flagged)}
            >
              <FlagLineIcon filled={Boolean(spa?.is_flagged)}/>
            </button>
          </Tooltip>
        );

      case 'edit':
        if (isLockedSpa(spa)) {
          return canRegisterSpa ? (
            <Tooltip title="Register spa" arrow>
              <button type="button" className="dt-act" onClick={()=>setSpaToRegister(spa)} aria-label="Register spa">
                <UserPlusIcon/>
              </button>
            </Tooltip>
          ) : na;
        }
        return (
          <Tooltip title="Edit spa" arrow>
            <button type="button" className="dt-act" onClick={()=>handleEditDelete(spa, 'edit')} aria-label="Edit spa">
              <PencilIcon/>
            </button>
          </Tooltip>
        );

      default:
        return null;
    }
  };

  const someChecked = !allChecked && (checked?.length ?? 0) > 0;

  return (
    <div className="dt">
      {spaToRegister &&
        <RegisterUserModal isOpen setOpen={()=>setSpaToRegister(null)} user={null} spa={spaToRegister}/>}

      <div className="dt-toolbar">
        <div className="dt-toolbar-start">{toolbar}</div>

        <button
          type="button"
          className="btn btn-sm dt-tool-btn"
          onClick={(e)=>setAnchorEl(e.currentTarget)}
          aria-haspopup="true"
          aria-expanded={open}
        >
          <ColumnsIcon/> Columns
        </button>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={()=>setAnchorEl(null)}
          closeAfterTransition={false}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { className: 'dt-popover' } }}
        >
          <div className="dt-columns">
            <span className="dt-columns-title">Show columns</span>
            <div className="dt-columns-grid">
              {headers.filter(showSetting).map((header)=>(
                <label key={header.id} className="dt-columns-item">
                  <input
                    type="checkbox"
                    className="dt-check"
                    checked={headersToShow.includes(header.id)}
                    onChange={(e)=>handleSetting(e, header.id)}
                  />
                  {header.label}
                </label>
              ))}
            </div>
          </div>
        </Popover>
      </div>

      <div className="dt-scroll">
        {isLoading ? (
          <div className="dt-state"><span className="spinner" role="progressbar" aria-label="Loading spas"/></div>
        ) : spas.length > 0 ? (
          <table className="dt-table">
            <thead>
              <tr>
                {visibleHeaders.map((header)=>(
                  <th
                    key={header.id}
                    scope="col"
                    className={cellClass(header)}
                    style={pinStyle(header.id)}
                    aria-sort={header.sortable && orderBy === header.id ? (order === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {header.id === 'checked' ? (
                      <input
                        type="checkbox"
                        id="all-checked"
                        className="dt-check"
                        checked={allChecked}
                        ref={(el)=>{ if (el) el.indeterminate = someChecked; }}
                        onChange={(e)=>checkAll(e)}
                        aria-label="Select all spas on this page"
                      />
                    ) : header.sortable ? (
                      <SortButton label={header.label} active={orderBy === header.id} order={order} onClick={()=>handleRequestSort(header.id)}/>
                    ) : header.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {spas.map((spa:any)=>(
                <tr key={spa.id} className={[!spa?.is_moved && 'v1', isChecked(spa) && 'selected'].filter(Boolean).join(' ') || undefined}>
                  {visibleHeaders.map((header)=>(
                    <td key={header.id} className={cellClass(header)} style={pinStyle(header.id)}>
                      {renderCell(header.id, spa)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="dt-state">
            {/* Not a <p>: Tables.css positions every `.list p` absolutely (user details) */}
            <div className="dt-empty-title">{localizeSpa("No Spas found")}</div>
          </div>
        )}
      </div>

      <DataTableFoot
        shown={spas?.length ?? 0}
        total={total}
        noun={localizeSpa(total === 1 ? 'Spa' : 'Spas')}
        page={page}
        totalPages={totalPages}
        loading={isLoading}
        onPage={goToPage}
      />
    </div>
  );
};


export default SpasTable;
